import { useMemo } from "react";

import {
  faExclamationCircle,
  faLock,
  faMagnifyingGlass,
} from "@fortawesome/pro-regular-svg-icons";
import { Column, createColumnHelper } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { DisplayDateFromNow } from "src/base-components/Date";
import { Cell, Header, TableComp } from "src/base-components/Table";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseAssignee } from "src/cases/common/CaseAssignee";
import { CaseStatusPillWithSchema } from "src/cases/common/CaseStatusPillWithSchema";
import {
  CASES_FOR_ENTITY_DEFAULT_SORT,
  useCasesForEntity,
} from "src/cases/queries/cases";
import { CasesDeserialized } from "src/cases/queries/casesTypes";
import { useCaseQueues } from "src/cases/queries/queues";
import { sortingStateToSortParam } from "src/cases/queues/hooks/filterParamUtils";
import { GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { IconButton } from "src/design-system/Icon";
import { useCasesSortParam } from "src/entities/entityView/useCasesSortParam";
import { useSidepane } from "src/entities/entityView/utils";
import { useTrackEntityFunnel } from "src/entities/useTrackEntityFunnel";
import { useEnvironment } from "src/hooks/useEnvironment";
import { EntityViewParams } from "src/router/urls";
import { getSortIcon } from "src/utils/icons";
import { useParamsDecode } from "src/utils/useParamsDecode";

type EntityCase = CasesDeserialized["data"][number];

const helper = createColumnHelper<EntityCase>();

const SortToggle: React.FC<{
  column: Column<EntityCase, unknown>;
}> = ({ column }) => {
  const isSorted = column.getIsSorted();

  return (
    <IconButton
      dataLoc={`entity-cases-sort-toggle-${column.id}`}
      icon={getSortIcon(isSorted)}
      isActive={!!isSorted}
      size="2xs"
      onClick={column.getToggleSortingHandler()}
    />
  );
};

const EntityCaseAssigneeCell: React.FC<{
  caseData: EntityCase;
}> = ({ caseData }) => {
  const queueId = caseData.queue?.id;

  if (!queueId) {
    return null;
  }

  return (
    // Stop the row click from also toggling the sidepane
    <div onClick={(e) => e.stopPropagation()}>
      <CaseAssignee
        assignee={caseData.assignee?.id}
        caseId={caseData.id}
        etag={caseData.etag}
        queueId={queueId}
      />
    </div>
  );
};

const buildColumns = (queueNameById: Record<string, string>) => [
  helper.accessor("id", {
    id: "id",
    header: (info) => (
      <Header dataLoc="entity-cases-header-id" info={info}>
        <span className="whitespace-nowrap">Case ID</span>
      </Header>
    ),
    cell: (info) => (
      <Cell className="w-24" info={info}>
        <TruncatedIdPill id={info.getValue()} variant="last-five" />
      </Cell>
    ),
  }),
  helper.accessor((row) => row.queue?.id, {
    id: "queue",
    header: (info) => (
      <Header dataLoc="entity-cases-header-queue" info={info}>
        Case Queue
      </Header>
    ),
    cell: (info) => {
      const queueId = info.getValue();
      return (
        <Cell info={info}>{queueId ? (queueNameById[queueId] ?? "") : ""}</Cell>
      );
    },
  }),
  helper.accessor((row) => row, {
    id: "status",
    header: (info) => (
      <Header dataLoc="entity-cases-header-status" info={info}>
        Status
      </Header>
    ),
    cell: (info) => {
      const { case_properties, template, template_version } = info.row.original;

      return (
        <Cell info={info}>
          <CaseStatusPillWithSchema
            status={case_properties.status}
            subStatus={case_properties.sub_status}
            templateId={template.id}
            versionId={template_version.id}
          />
        </Cell>
      );
    },
  }),
  helper.accessor((row) => row.assignee?.id, {
    id: "assignee",
    header: (info) => (
      <Header dataLoc="entity-cases-header-assignee" info={info}>
        Assigned to
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <EntityCaseAssigneeCell caseData={info.row.original} />
      </Cell>
    ),
  }),
  helper.accessor("created_at", {
    id: "created_at",
    enableSorting: true,
    header: (info) => (
      <Header dataLoc="entity-cases-header-created-at" info={info}>
        <div className="flex items-center gap-x-1">
          <span className="whitespace-nowrap">Created at</span>
          <SortToggle column={info.column} />
        </div>
      </Header>
    ),
    cell: (info) => (
      <Cell className="font-inter-normal-12px" info={info}>
        <DisplayDateFromNow value={info.getValue()} />
      </Cell>
    ),
  }),
];

type EntityCasesProps = {
  entityId: string;
};

export const EntityCases: React.FC<EntityCasesProps> = ({ entityId }) => {
  const { schema } = useParamsDecode<EntityViewParams>();
  const [environment] = useEnvironment();
  const { trackRelatedCaseDetailOpened } = useTrackEntityFunnel(schema);

  const [sorting, onSortingChange] = useCasesSortParam();

  const {
    data: cases,
    isFetching,
    isError,
    error,
    fetchNextPage,
    isFetchingNextPage,
  } = useCasesForEntity({
    entityId,
    entityType: schema,
    environment:
      environment as GetCasesByEntityApiV1CasesRelatedGetFilterEnvEnum,
    sort: sortingStateToSortParam(sorting) ?? CASES_FOR_ENTITY_DEFAULT_SORT,
  });

  const isForbidden = error?.response?.status === 403;

  const { resourceId: selectedCaseId, toggleSidepane } = useSidepane("case");

  const { data: queues } = useCaseQueues();
  const queueNameById = useMemo(
    () =>
      Object.fromEntries((queues ?? []).map((queue) => [queue.id, queue.name])),
    [queues],
  );

  const columns = useMemo(() => buildColumns(queueNameById), [queueNameById]);

  const isTableLoading = isFetching && !isFetchingNextPage;

  const hasCases = !isTableLoading && !!cases?.length;
  const isEmpty = !isTableLoading && !isError && !hasCases;

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4">
      <TableComp
        columns={columns}
        data={cases ?? []}
        dataLoc="entity-cases-table"
        frameClassName={twJoin("w-full", (isEmpty || isError) && "h-auto")}
        getRowId={(row) => row.id}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isTableLoading}
        manualSorting
        sorting={sorting}
        rowPropsGetter={(row) => ({
          "data-loc": `entity-case-row-${row.id}`,
          onClick: () => {
            if (row.id !== selectedCaseId) {
              trackRelatedCaseDetailOpened(entityId, row.id);
            }
            toggleSidepane({ id: row.id });
          },
          classNameOverrides: twJoin(
            "cursor-pointer",
            row.id === selectedCaseId
              ? "selected bg-gray-50"
              : "hover:bg-gray-50",
          ),
        })}
        variant="compact"
        onScrolledToTheBottom={fetchNextPage}
        onSortingChange={onSortingChange}
      />
      {isError && (
        <div className="h-96">
          <EmptyState
            dataLoc="entity-cases-error-state"
            description={
              isForbidden
                ? "You don't have permission to view Cases for this Entity"
                : "We're having trouble loading Cases. Try refreshing the page"
            }
            headline={isForbidden ? "Access restricted" : "Error loading Cases"}
            icon={isForbidden ? faLock : faExclamationCircle}
            variant="error"
          />
        </div>
      )}
      {isEmpty && (
        <div className="h-96" data-loc="entity-cases-empty-state">
          <EmptyState
            description="No Cases are related to this Entity yet"
            headline="No Cases found"
            icon={faMagnifyingGlass}
          />
        </div>
      )}
    </div>
  );
};

export const CasesTabContent: React.FC = () => {
  const { id } = useParamsDecode<EntityViewParams>();

  return (
    <div className="p-5">
      <EntityCases entityId={id} />
    </div>
  );
};
